import {
  AnalyticsReportingMode,
  getAnalyticsReportingMode,
  usesLegacyAnalyticsUi,
} from './analyticsReportingMode';

type SessionWithReportingMode = Parameters<typeof getAnalyticsReportingMode>[0];

type SessionWithLegacyAutomatedReportCount = SessionWithReportingMode & {
  isPartner?: boolean;
  /** Client account on client sessions; partner sessions also expose a `client` alias. */
  client?: {
    id?: string | number;
    legacyAutomatedReportCount?: number | null;
  };
};

export function getLegacyAutomatedReportCount(
  sessionPlData?: SessionWithLegacyAutomatedReportCount,
): number | null | undefined {
  if (sessionPlData?.isPartner) {
    return undefined;
  }
  return sessionPlData?.client?.legacyAutomatedReportCount;
}

export function canCreateAutomatedReport(
  sessionPlData?: SessionWithReportingMode & { isPartner?: boolean },
): boolean {
  if (sessionPlData?.isPartner) {
    return true;
  }
  return usesLegacyAnalyticsUi(getAnalyticsReportingMode(sessionPlData));
}

export function getAutomatedReportingNavState({
  isPartner,
  reportingMode,
  legacyAutomatedReportCount,
}: {
  isPartner?: boolean;
  reportingMode: AnalyticsReportingMode;
  legacyAutomatedReportCount?: number | null;
}): { hidden: boolean; deprecated: boolean } {
  if (isPartner) {
    return { hidden: false, deprecated: false };
  }

  if (usesLegacyAnalyticsUi(reportingMode)) {
    return { hidden: false, deprecated: false };
  }

  // Until session.pl provides a count, keep pre-gating nav behaviour.
  if (legacyAutomatedReportCount == null) {
    return { hidden: false, deprecated: false };
  }

  if (legacyAutomatedReportCount === 0) {
    return { hidden: true, deprecated: false };
  }

  return { hidden: false, deprecated: true };
}

export function getAutomatedReportingNavStateForSession(
  sessionPlData?: SessionWithLegacyAutomatedReportCount,
) {
  return getAutomatedReportingNavState({
    isPartner: sessionPlData?.isPartner,
    reportingMode: getAnalyticsReportingMode(sessionPlData),
    legacyAutomatedReportCount: getLegacyAutomatedReportCount(sessionPlData),
  });
}

export function getAutomatedReportingNavName(
  baseName: string,
  navState: ReturnType<typeof getAutomatedReportingNavState>,
): string {
  if (navState.deprecated) {
    return `${baseName} (deprecated)`;
  }
  return baseName;
}

export function isAutomatedReportingDeprecatedForSession(
  sessionPlData?: SessionWithLegacyAutomatedReportCount,
): boolean {
  return getAutomatedReportingNavStateForSession(sessionPlData).deprecated;
}

export function getAutomatedReportingPageTitle(
  sessionPlData?: SessionWithLegacyAutomatedReportCount,
  suffix?: string,
): string {
  const baseTitle = isAutomatedReportingDeprecatedForSession(sessionPlData)
    ? 'Automated Reporting (deprecated)'
    : 'Automated Reporting';
  return suffix ? `${baseTitle} - ${suffix}` : baseTitle;
}
